import {noop} from "lodash";
import {checkTerms} from "modules/actions/user";
import {RequestState} from "modules/enums";
import {
	getIsShowOntarioTerms,
	getIsShowTermsModal,
	getIsTermsAccepted,
	loadingTermsState,
} from "modules/selectors";
import {useAdobeDTM} from "modules/utils/hooks";
import React, {SyntheticEvent, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import styled from "@emotion/styled";
import {TermsNonBetting} from "components/CheckTerms/termsNonBetting";
import {TermsUSABetting} from "components/CheckTerms/termsUSABetting";
import {TermsONBetting} from "components/CheckTerms/termsONBetting";

const Wrapper = styled.div`
	position: relative;
	height: 100vh;
`;

const Modal = styled.div`
	position: fixed;
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	background: rgba(9, 11, 15, 0.9);
	z-index: 9999;
	display: flex;
	justify-content: center;
	align-items: center;
`;

const ModalBody = styled.div`
	@keyframes fadeIn {
		from {
			opacity: 0;
		}

		to {
			opacity: 1;
		}
	}
	width: 100%;
	max-width: 540px;
	max-height: 90vh;
	background: #fff;
	position: relative;
	border-radius: 4px;
	box-sizing: border-box;
	padding: 20px 30px;
	animation: fadeIn 0.5s linear;
	overflow: auto;

	@media (max-width: 600px) {
		max-width: 90%;
	}

	.abbrev-rules {
		text-align: center;
		margin: 5px;
		font-size: 11px;
		color: #000;

		a {
			color: #2687ff;
		}
	}
`;

interface ICheckTerms {
	uid: string;
	onSubmit: (isOptIn: boolean, acceptTerms: false) => void;
}

const PAGE_NAME = "mlb play : beat the streak : terms & conditions";

export const CheckTerms: React.FC<Partial<ICheckTerms>> = ({uid = "", onSubmit = noop}) => {
	const dispatch = useDispatch();
	const {trackAction, trackState} = useAdobeDTM();
	const isTermsAccepted = useSelector(getIsTermsAccepted);
	const loadingState = useSelector(loadingTermsState);
	const [isChecked, setIsChecked] = useState<boolean>(false);
	const isShowOntarioTerms = useSelector(getIsShowOntarioTerms);
	const isShowTermsModal = useSelector(getIsShowTermsModal);
	const isTermsLoaded = loadingState === RequestState.Received;

	useEffect(() => {
		if (loadingState === RequestState.Received && !isTermsAccepted) {
			trackState({
				"page.name": PAGE_NAME,
				"page.type": "beat the streak",
				"page.subType": "terms & conditions",
			});
		}
	}, [isTermsAccepted, loadingState]);

	useEffect(() => {
		if (uid) {
			dispatch(checkTerms({uid}));
		}
	}, [dispatch, uid]);

	useEffect(() => {
		if (isTermsAccepted) {
			onSubmit(isChecked, false);
		}
	}, [isTermsAccepted]);

	const trackActionRules = () => {
		trackAction({
			"action.name": "mlb play : beat the streak : terms & conditions : official rules click",
			"action.element.text": "official rules",
			"action.element.index": 0,
			"page.name": PAGE_NAME,
		});
	};

	const handlerChange = (e: SyntheticEvent<HTMLInputElement>) => {
		setIsChecked(e.currentTarget.checked);
	};

	const onPlay = () => {
		trackAction({
			"action.name": "mlb play : beat the streak : terms & conditions : play click",
			"action.element.text": "play",
			"action.element.index": 0,
			"page.name": PAGE_NAME,
		});
		onSubmit(isChecked, true);
	};

	if (isShowOntarioTerms && isTermsLoaded) {
		return (
			<Wrapper>
				<Modal>
					<ModalBody>
						<TermsONBetting
							onPlay={onPlay}
							trackActionRules={trackActionRules}
							handlerChange={handlerChange}
						/>
					</ModalBody>
				</Modal>
			</Wrapper>
		);
	}

	if (!isShowTermsModal && uid.length) {
		return null;
	}

	return isTermsLoaded || !uid.length ? (
		<Wrapper>
			<Modal>
				<ModalBody>
					<TermsNonBetting onPlay={onPlay} trackActionRules={trackActionRules} />
					<TermsUSABetting
						onPlay={onPlay}
						trackActionRules={trackActionRules}
						handlerChange={handlerChange}
					/>
				</ModalBody>
			</Modal>
		</Wrapper>
	) : null;
};
